import React from 'react';

type TouchHandler = (e: React.TouchEvent) => void;
type MouseHandler = (e: React.MouseEvent) => void;

interface GetTouchMouseEventHandlersArgs {
    onTouchStart?: TouchHandler,
    onTouchMove?: TouchHandler,
    onTouchEnd?: TouchHandler,
    onTouchCancel?: TouchHandler,
    onMouseEnter?: MouseHandler,
    onMouseMove?: MouseHandler,
    onMouseLeave?: MouseHandler,
    onMouseUp?: MouseHandler,
    suppressMouseAfterTouchMs?: number,
}

interface TouchMouseEventHandlers {
    onTouchStart?: TouchHandler,
    onTouchMove?: TouchHandler,
    onTouchEnd?: TouchHandler,
    onTouchCancel?: TouchHandler,
    onTouchStartCapture?: TouchHandler,
    onTouchEndCapture?: TouchHandler,
    onMouseEnter?: MouseHandler,
    onMouseMove?: MouseHandler,
    onMouseLeave?: MouseHandler,
    onMouseUp?: MouseHandler,
}

let lastTouchInteractionTimestamp = 0;
const DEFAULT_SUPPRESS_MOUSE_AFTER_TOUCH_MS = 700;

const markTouchInteraction = (): void => {
    lastTouchInteractionTimestamp = Date.now();
};

const getTouchMouseEventHandlers = ({
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
    onMouseEnter,
    onMouseMove,
    onMouseLeave,
    onMouseUp,
    suppressMouseAfterTouchMs = DEFAULT_SUPPRESS_MOUSE_AFTER_TOUCH_MS,
}: GetTouchMouseEventHandlersArgs): TouchMouseEventHandlers => {
    if (
        !onTouchStart
        && !onTouchMove
        && !onTouchEnd
        && !onTouchCancel
        && !onMouseEnter
        && !onMouseMove
        && !onMouseLeave
        && !onMouseUp
    ) {
        return {};
    }

    const shouldSuppressMouseEvent = (): boolean => (
        Date.now() - lastTouchInteractionTimestamp < suppressMouseAfterTouchMs
    );
    const resolvedTouchCancelHandler = onTouchCancel || onTouchEnd;

    return {
        ...(onTouchStart
            ? {
                onTouchStart: (e: React.TouchEvent) => {
                    markTouchInteraction();
                    onTouchStart(e);
                },
            }
            : {}),
        ...(!onTouchStart && onMouseEnter
            ? {
                onTouchStartCapture: (e: React.TouchEvent) => {
                    markTouchInteraction();
                    (onMouseEnter as unknown as (event: React.TouchEvent) => void)(e);
                },
            }
            : {}),
        ...(onTouchMove
            ? {
                onTouchMove: (e: React.TouchEvent) => {
                    markTouchInteraction();
                    onTouchMove(e);
                },
            }
            : {}),
        ...(onTouchEnd
            ? {
                onTouchEnd: (e: React.TouchEvent) => {
                    markTouchInteraction();
                    onTouchEnd(e);
                },
            }
            : {}),
        ...(!onTouchEnd && onMouseLeave
            ? {
                onTouchEndCapture: (e: React.TouchEvent) => {
                    markTouchInteraction();
                    (onMouseLeave as unknown as (event: React.TouchEvent) => void)(e);
                },
            }
            : {}),
        ...(resolvedTouchCancelHandler
            ? {
                onTouchCancel: (e: React.TouchEvent) => {
                    markTouchInteraction();
                    resolvedTouchCancelHandler(e);
                },
            }
            : {}),
        ...(onMouseEnter
            ? {
                onMouseEnter: (e: React.MouseEvent) => {
                    if (shouldSuppressMouseEvent()) return;
                    onMouseEnter(e);
                },
            }
            : {}),
        ...(onMouseMove
            ? {
                onMouseMove: (e: React.MouseEvent) => {
                    if (shouldSuppressMouseEvent()) return;
                    onMouseMove(e);
                },
            }
            : {}),
        ...(onMouseLeave
            ? {
                onMouseLeave: (e: React.MouseEvent) => {
                    onMouseLeave(e);
                },
            }
            : {}),
        ...(onMouseUp
            ? {
                onMouseUp: (e: React.MouseEvent) => {
                    onMouseUp(e);
                },
            }
            : {}),
    };
};

export default getTouchMouseEventHandlers;
